// errorReportingService.ts
// Centralized error reporting for production monitoring

import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';
import Constants from 'expo-constants';

// Import crashlytics modularly (only on native)
let crashlytics: any = null;
if (Platform.OS !== 'web') {
  const crashlyticsModule = require('@react-native-firebase/crashlytics');
  // Get the crashlytics instance once (modular API - no function call needed)
  crashlytics = crashlyticsModule.default();
}

interface ErrorContext {
  userId?: string;
  screen?: string;
  action?: string;
  metadata?: Record<string, any>;
}

class ErrorReportingService {
  private isDevelopment = __DEV__;
  private isInitialized = false;

  /**
   * Initialize error reporting services
   * Call this once at app startup
   */
  initialize(sentryDsn?: string) {
    if (this.isInitialized) {
      console.log('⚠️ Sentry already initialized, skipping...');
      return;
    }

    console.log('🔧 Initializing error reporting...', { 
      hasDsn: !!sentryDsn, 
      isDev: __DEV__,
      platform: Platform.OS 
    });

    // Initialize Sentry for all platforms (especially web)
    if (sentryDsn) {
      try {
        console.log('🚀 Starting Sentry initialization...');
        
        // Get app version for release tracking
        const appVersion = Constants.expoConfig?.version || '1.0.0';
        const buildNumber = Constants.expoConfig?.ios?.buildNumber || 
                           Constants.expoConfig?.android?.versionCode || '1';
        const release = `mentorist-rn@${appVersion}+${buildNumber}`;
        
        Sentry.init({
          dsn: sentryDsn,
          environment: __DEV__ ? 'development' : 'production',
          release: release, // Add release for session tracking
          debug: __DEV__,
          tracesSampleRate: __DEV__ ? 1.0 : 0.1, // Sample 100% in dev, 10% in prod
          enableAutoSessionTracking: true,
          sessionTrackingIntervalMillis: 10000,
          // Only enabled in production to save quota
          enabled: !__DEV__,
        });
        console.log('✅ Sentry initialized successfully!', { release });
      } catch (error) {
        console.error('❌ Failed to initialize Sentry:', error);
      }
    } else {
      console.warn('⚠️ Sentry DSN not provided - error monitoring disabled');
      console.warn('💡 Add EXPO_PUBLIC_SENTRY_DSN to your .env file');
    }

    // Initialize Firebase Crashlytics (mobile only)
    if (Platform.OS !== 'web' && crashlytics) {
      try {
        crashlytics.setCrashlyticsCollectionEnabled(!__DEV__);
        console.log('✅ Firebase Crashlytics initialized');
      } catch (error) {
        console.warn('⚠️ Failed to initialize Crashlytics:', error);
      }
    }

    this.isInitialized = true;
  }

  /**
   * Set user context for error reports
   */
  setUser(userId: string | null, email?: string, username?: string) {
    if (!userId) {
      this.clearUser();
      return;
    }

    // Set user in Sentry
    Sentry.setUser({
      id: userId,
      email,
      username,
    });

    // Set user in Crashlytics (mobile only)
    if (Platform.OS !== 'web' && crashlytics) {
      try {
        crashlytics.setUserId(userId);
        if (email) {
          crashlytics.setAttribute('email', email);
        }
        if (username) {
          crashlytics.setAttribute('username', username);
        }
      } catch (error) {
        console.warn('⚠️ Failed to set Crashlytics user:', error);
      }
    }
  }

  /**
   * Clear user context (e.g., on logout)
   */
  clearUser() {
    Sentry.setUser(null);
    // Crashlytics doesn't have a clear method, set to "anonymous"
    if (Platform.OS !== 'web' && crashlytics) {
      try {
        crashlytics.setUserId('anonymous');
      } catch (error) {
        console.warn('⚠️ Failed to clear Crashlytics user:', error);
      }
    }
  }

  /**
   * Report critical errors that block user flow
   * 
   * Always logs to console for local debugging AND sends to Sentry in production.
   * Use this for errors that indicate bugs or issues users face.
   * 
   * @example
   * try {
   *   await processPayment();
   * } catch (error) {
   *   errorReporting.reportError(error, {
   *     userId: user.uid,
   *     screen: 'CheckoutScreen',
   *     action: 'processPayment',
   *     metadata: { amount: total }
   *   });
   * }
   */
  reportError(error: Error | unknown, context: ErrorContext) {
    const errorInfo = this.formatError(error);
    
    // Always log to console with full context (for local debugging)
    console.error(`🚨 Error in ${context.screen || 'unknown screen'}:`, {
      error: errorInfo,
      context,
      timestamp: new Date().toISOString(),
    });

    // In production, send to monitoring services
    if (!this.isDevelopment) {
      this.sendToMonitoringService(error, {
        ...errorInfo,
        ...context,
        severity: 'error',
      });
    }
  }

  /**
   * Report API format issues and non-critical problems
   */
  reportWarning(message: string, details: any, context: ErrorContext) {
    // Always log warnings
    console.warn(`⚠️ Warning in ${context.screen || 'unknown screen'}:`, {
      message,
      details,
      context,
      timestamp: new Date().toISOString(),
    });

    // Track in analytics
    this.trackAnalyticsEvent('api_format_warning', {
      message,
      screen: context.screen,
      ...context.metadata,
    });

    // In production, send to monitoring
    if (!this.isDevelopment) {
      // Send as message (not exception) with warning level
      Sentry.captureMessage(message, {
        level: 'warning',
        contexts: {
          warning: {
            details,
            ...context,
          },
        },
        tags: {
          screen: context.screen,
        },
      });

      // Crashlytics: log as non-fatal
      if (Platform.OS !== 'web') {
        try {
          crashlytics?.log(`Warning: ${message}`);
          crashlytics?.recordError(new Error(message));
        } catch (error) {
          console.warn('Failed to log warning to Crashlytics:', error);
        }
      }
    }
  }

  /**
   * Report API response validation failures
   */
  reportApiValidationError(
    endpoint: string,
    expectedFormat: any,
    actualResponse: any,
    context: ErrorContext
  ) {
    const validationError = {
      endpoint,
      expectedFormat,
      actualResponse,
      timestamp: new Date().toISOString(),
    };

    console.error('🔴 API Validation Error:', validationError);

    // This is critical - always report to monitoring
    const error = new Error(`API Validation Error: ${endpoint}`);
    this.sendToMonitoringService(error, {
      type: 'api_validation_error',
      ...validationError,
      ...context,
      severity: 'critical',
    });

    // Also track in analytics for monitoring API health
    this.trackAnalyticsEvent('api_validation_error', {
      endpoint,
      screen: context.screen,
    });
  }

  /**
   * Track non-error events for monitoring
   */
  trackEvent(eventName: string, properties?: Record<string, any>) {
    if (this.isDevelopment) {
      console.log(`📊 Event: ${eventName}`, properties);
    }
    
    this.trackAnalyticsEvent(eventName, properties);
  }

  private formatError(error: Error | unknown): Record<string, any> {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    
    return {
      name: 'UnknownError',
      message: String(error),
      raw: error,
    };
  }

  private sendToMonitoringService(error: Error | unknown, data: any) {
    try {
      // Add breadcrumbs for context
      Sentry.addBreadcrumb({
        category: data.screen || 'unknown',
        message: data.action || 'error_occurred',
        level: data.severity === 'critical' ? 'fatal' : 'error',
        data: data.metadata,
      });

      // Set tags for filtering
      if (data.screen) {
        Sentry.setTag('screen', data.screen);
      }
      if (data.action) {
        Sentry.setTag('action', data.action);
      }
      if (data.userId) {
        Sentry.setTag('userId', data.userId);
      }

      // Set context
      Sentry.setContext('error_context', {
        ...data,
        platform: Platform.OS,
        timestamp: new Date().toISOString(),
      });

      // Capture exception in Sentry
      if (error instanceof Error) {
        Sentry.captureException(error, {
          level: data.severity === 'critical' ? 'fatal' : 'error',
        });
      } else {
        Sentry.captureMessage(String(error), {
          level: data.severity === 'critical' ? 'fatal' : 'error',
        });
      }

      // Also log to Firebase Crashlytics (mobile only)
      if (Platform.OS !== 'web') {
        try {
          // Add custom keys for filtering
          if (data.screen) crashlytics?.setAttribute('screen', data.screen);
          if (data.action) crashlytics?.setAttribute('action', data.action);
          if (data.userId) crashlytics?.setAttribute('userId', data.userId);
          
          // Log breadcrumb
          crashlytics?.log(`${data.screen || 'unknown'}: ${data.action || 'error'}`);
          
          // Record the error
          if (error instanceof Error) {
            crashlytics?.recordError(error);
          } else {
            crashlytics?.recordError(new Error(String(error)));
          }
        } catch (crashlyticsError) {
          console.warn('⚠️ Failed to send to Crashlytics:', crashlyticsError);
        }
      }

      console.log('📤 Error sent to monitoring services:', {
        platform: Platform.OS,
        sentry: true,
        crashlytics: Platform.OS !== 'web',
      });
    } catch (monitoringError) {
      // Never let error reporting crash the app
      console.error('🚨 Failed to send error to monitoring:', monitoringError);
    }
  }

  private trackAnalyticsEvent(eventName: string, properties?: Record<string, any>) {
    // Add breadcrumb to Sentry for debugging context
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: eventName,
      level: 'info',
      data: properties,
    });

    // Log to Firebase Analytics via your existing logging service
    // This integrates with your existing loggingService.ts
    if (this.isDevelopment) {
      console.log(`📈 Analytics: ${eventName}`, properties);
    }
  }
}

export const errorReporting = new ErrorReportingService(); 